import React, { useRef, useEffect, useCallback } from "react";
import { BaseAppProps } from "../../../types/webos";

interface DrawingPoint {
  x: number;
  y: number;
  timestamp: number;
  touchId?: number;
}

interface DrawingStroke {
  points: DrawingPoint[];
  color: string;
  startTime: number;
  touchId?: number;
}

export function App({ onClose }: BaseAppProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<DrawingStroke[]>([]);
  const activeStrokesRef = useRef<Map<number, DrawingStroke>>(new Map());
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Generate a random vibrant color
  const generateColor = useCallback(() => {
    const hue = Math.random() * 360;
    const saturation = 70 + Math.random() * 30; // 70-100%
    const lightness = 45 + Math.random() * 20; // 45-65%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }, []);

  // Get touch/mouse position relative to canvas
  const getEventPosition = useCallback(
    (event: TouchEvent | MouseEvent, canvas: HTMLCanvasElement) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ("touches" in event) {
        // Touch event
        return Array.from(event.touches).map((touch, index) => ({
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
          touchId: touch.identifier,
          timestamp: Date.now(),
        }));
      } else {
        // Mouse event
        return [
          {
            x: (event.clientX - rect.left) * scaleX,
            y: (event.clientY - rect.top) * scaleY,
            touchId: 0, // Use 0 for mouse
            timestamp: Date.now(),
          },
        ];
      }
    },
    []
  );

  // Start drawing
  const handleStart = useCallback(
    (event: TouchEvent | MouseEvent) => {
      event.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const positions = getEventPosition(event, canvas);

      positions.forEach((pos) => {
        const stroke: DrawingStroke = {
          points: [pos],
          color: generateColor(),
          startTime: Date.now(),
          touchId: pos.touchId,
        };

        activeStrokesRef.current.set(pos.touchId!, stroke);
      });
    },
    [getEventPosition, generateColor]
  );

  // Continue drawing
  const handleMove = useCallback(
    (event: TouchEvent | MouseEvent) => {
      event.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const positions = getEventPosition(event, canvas);

      positions.forEach((pos) => {
        const activeStroke = activeStrokesRef.current.get(pos.touchId!);
        if (activeStroke) {
          activeStroke.points.push(pos);
        }
      });
    },
    [getEventPosition]
  );

  // End drawing
  const handleEnd = useCallback((event: TouchEvent | MouseEvent) => {
    event.preventDefault();

    if ("changedTouches" in event) {
      // Touch event
      Array.from(event.changedTouches).forEach((touch) => {
        const activeStroke = activeStrokesRef.current.get(touch.identifier);
        if (activeStroke) {
          strokesRef.current.push(activeStroke);
          activeStrokesRef.current.delete(touch.identifier);
        }
      });
    } else {
      // Mouse event
      const activeStroke = activeStrokesRef.current.get(0);
      if (activeStroke) {
        strokesRef.current.push(activeStroke);
        activeStrokesRef.current.delete(0);
      }
    }
  }, []);

  // Draw a stroke on the canvas
  const drawStroke = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      stroke: DrawingStroke,
      opacity: number = 1
    ) => {
      if (stroke.points.length < 2) return;

      ctx.globalAlpha = opacity;
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = 10;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);

      for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
      }

      ctx.stroke();
    },
    []
  );

  // Clear canvas function
  const clearCanvas = useCallback(() => {
    strokesRef.current = [];
    activeStrokesRef.current.clear();
  }, []);

  // Animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw completed strokes (no fade effect)
    strokesRef.current.forEach((stroke) => {
      drawStroke(ctx, stroke, 1);
    });

    // Draw active strokes
    activeStrokesRef.current.forEach((stroke) => {
      drawStroke(ctx, stroke, 1);
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [drawStroke]);

  // Resize canvas to fill container
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentElement;
    if (!container) return;

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set up canvas size
    resizeCanvas();

    // Add event listeners
    canvas.addEventListener("touchstart", handleStart, { passive: false });
    canvas.addEventListener("touchmove", handleMove, { passive: false });
    canvas.addEventListener("touchend", handleEnd, { passive: false });
    canvas.addEventListener("mousedown", handleStart);
    canvas.addEventListener("mousemove", handleMove);
    canvas.addEventListener("mouseup", handleEnd);

    // Handle window resize
    window.addEventListener("resize", resizeCanvas);

    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      // Cleanup
      canvas.removeEventListener("touchstart", handleStart);
      canvas.removeEventListener("touchmove", handleMove);
      canvas.removeEventListener("touchend", handleEnd);
      canvas.removeEventListener("mousedown", handleStart);
      canvas.removeEventListener("mousemove", handleMove);
      canvas.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("resize", resizeCanvas);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleStart, handleMove, handleEnd, resizeCanvas, animate]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: "#000",
        position: "relative",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          touchAction: "none", // Prevent scrolling on touch
          cursor: "crosshair",
          background: "white",
        }}
      />
      <button
        onClick={clearCanvas}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          padding: "8px 16px",
          backgroundColor: "#ff4444",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "bold",
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#ff6666";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#ff4444";
        }}
      >
        Clear
      </button>
    </div>
  );
}
